import { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, API } from "../lib/api";
import { AdminGate, AdminShell } from "./AdminGate";
import { toast } from "sonner";
import { CheckCircle2, XCircle, MessageCircle, Send, Ban, RefreshCw, Wallet, Users, Images, Trophy, UserCheck } from "lucide-react";

const inputCls =
  "w-full px-4 py-3 rounded-xl border border-slate-200 bg-white text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-[#F5B301] placeholder:text-slate-400";

const brl = (v) => `R$ ${Number(v || 0).toFixed(2).replace(".", ",")}`;
const fmtDt = (iso) => (iso ? new Date(iso).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : "—");
const fotoUrl = (idPublico) => `${API}/indicador-foto/${idPublico}`;
const waLink = (tel) => {
  const d = (tel || "").replace(/\D/g, "");
  if (d.length < 10) return null;
  return `https://wa.me/${d.startsWith("55") ? d : `55${d}`}`;
};

const STATUS_STYLE = {
  aguardando_aprovacao: "bg-amber-100 text-amber-700",
  ativo: "bg-emerald-100 text-emerald-700",
  rejeitado: "bg-red-100 text-red-600",
  suspenso: "bg-slate-200 text-slate-600",
  bloqueado: "bg-red-600 text-white",
};
const COM_STYLE = {
  aguardando_conclusao: "bg-slate-100 text-slate-600",
  liberada: "bg-blue-100 text-blue-700",
  paga: "bg-emerald-100 text-emerald-700",
  cancelada: "bg-red-100 text-red-600",
};

function IndicadoresTab() {
  const [stats, setStats] = useState(null);
  const [lista, setLista] = useState([]);
  const [filtros, setFiltros] = useState({ nome: "", id: "", status: "", de: "", ate: "" });
  const [rejeitarId, setRejeitarId] = useState(null);
  const [motivo, setMotivo] = useState("");

  const load = useCallback(() => {
    api.get("/admin/indicadores/stats").then((r) => setStats(r.data)).catch(() => {});
    const params = Object.fromEntries(Object.entries(filtros).filter(([, v]) => v));
    api.get("/admin/indicadores", { params }).then((r) => setLista(r.data.indicadores || [])).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtros]);

  useEffect(() => { load(); }, [load]);

  const aprovar = async (ind) => {
    if (!window.confirm(`Aprovar o cadastro de ${ind.nome} (${ind.id_publico})? Isso libera login, link de indicação e carteira.`)) return;
    try {
      await api.post(`/admin/indicadores/${ind.indicador_id}/aprovar`);
      toast.success(`${ind.nome} aprovado!`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Falha ao aprovar");
    }
  };

  const rejeitar = async (ind) => {
    try {
      await api.post(`/admin/indicadores/${ind.indicador_id}/rejeitar`, { motivo });
      toast.success("Cadastro rejeitado.");
      setRejeitarId(null);
      setMotivo("");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Falha ao rejeitar");
    }
  };

  return (
    <div className="px-4 space-y-4" data-testid="admin-indicadores-tab">
      {stats && (
        <div className="grid grid-cols-3 gap-2" data-testid="indicadores-stats">
          {[
            { l: "Cadastrados", v: stats.total, tid: "stat-total" },
            { l: "Ativos", v: stats.ativos, tid: "stat-ativos" },
            { l: "Aguardando", v: stats.aguardando, tid: "stat-aguardando" },
            { l: "Suspensos", v: stats.suspensos, tid: "stat-suspensos" },
            { l: "Rejeitados", v: stats.rejeitados, tid: "stat-rejeitados" },
            { l: "Bloqueados", v: stats.bloqueados, tid: "stat-bloqueados" },
            { l: "C/ pagtos pendentes", v: stats.com_pagamentos_pendentes, tid: "stat-pag-pendentes" },
            { l: "Comissões liberadas", v: brl(stats.comissoes_liberadas_total), tid: "stat-com-liberadas" },
          ].map((s) => (
            <div key={s.tid} data-testid={s.tid} className="bg-white rounded-xl border border-slate-100 shadow-sm p-2.5 text-center">
              <p className="text-base font-heading font-extrabold text-navy">{s.v}</p>
              <p className="text-[8.5px] font-semibold text-slate-500 uppercase tracking-wide leading-tight">{s.l}</p>
            </div>
          ))}
        </div>
      )}

      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-3 space-y-2" data-testid="indicadores-filtros">
        <div className="grid grid-cols-2 gap-2">
          <input className={inputCls} placeholder="Buscar por nome" data-testid="filtro-nome" value={filtros.nome} onChange={(e) => setFiltros((f) => ({ ...f, nome: e.target.value }))} />
          <input className={inputCls} placeholder="Buscar por ID (IND-...)" data-testid="filtro-id" value={filtros.id} onChange={(e) => setFiltros((f) => ({ ...f, id: e.target.value }))} />
        </div>
        <div className="grid grid-cols-3 gap-2">
          <select className={inputCls} data-testid="filtro-status" value={filtros.status} onChange={(e) => setFiltros((f) => ({ ...f, status: e.target.value }))}>
            <option value="">Todos os status</option>
            <option value="aguardando_aprovacao">Aguardando aprovação</option>
            <option value="ativo">Ativo</option>
            <option value="suspenso">Suspenso</option>
            <option value="rejeitado">Rejeitado</option>
            <option value="bloqueado">Bloqueado</option>
          </select>
          <input type="date" className={inputCls} data-testid="filtro-de" value={filtros.de} onChange={(e) => setFiltros((f) => ({ ...f, de: e.target.value }))} />
          <input type="date" className={inputCls} data-testid="filtro-ate" value={filtros.ate} onChange={(e) => setFiltros((f) => ({ ...f, ate: e.target.value }))} />
        </div>
      </div>

      <div className="space-y-2" data-testid="indicadores-lista">
        {lista.length === 0 && <p className="text-xs text-slate-400 text-center py-6">Nenhum indicador encontrado.</p>}
        {lista.map((ind) => (
          <div key={ind.indicador_id} className="bg-white rounded-2xl border border-slate-100 shadow-sm p-3" data-testid={`indicador-row-${ind.id_publico}`}>
            <div className="flex items-center gap-3">
              {ind.foto ? (
                <img src={fotoUrl(ind.id_publico)} alt="" className="w-11 h-11 rounded-full object-cover border border-slate-200" onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = "/logo.jpg"; e.currentTarget.classList.remove("object-cover"); e.currentTarget.classList.add("object-contain", "bg-white", "p-1"); }} />
              ) : (
                <div className="w-11 h-11 rounded-full bg-navy text-[#F5B301] flex items-center justify-center font-extrabold">{ind.nome?.[0]}</div>
              )}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-bold text-navy truncate">{ind.nome}</p>
                <p className="text-[10px] text-slate-500">{ind.id_publico} · CPF {ind.cpf}</p>
                <p className="text-[10px] text-slate-400 truncate">{ind.email} · cadastro {fmtDt(ind.created_at)}</p>
              </div>
              <span className={`text-[9px] font-extrabold px-2 py-1 rounded-full whitespace-nowrap ${STATUS_STYLE[ind.status]}`} data-testid={`status-${ind.id_publico}`}>
                {ind.status_label}
              </span>
            </div>
            <div className="grid grid-cols-4 gap-1.5 mt-2.5 text-center">
              {[
                { l: "Indicados", v: ind.total_indicados },
                { l: "Contratados", v: ind.total_contratados },
                { l: "Liberadas", v: brl(ind.comissoes_liberadas) },
                { l: "Pagas", v: brl(ind.comissoes_pagas) },
              ].map((a) => (
                <div key={a.l} className="bg-slate-50 rounded-lg py-1.5">
                  <p className="text-[11px] font-extrabold text-navy">{a.v}</p>
                  <p className="text-[8px] text-slate-400 uppercase font-semibold">{a.l}</p>
                </div>
              ))}
            </div>
            <div className="flex gap-1.5 mt-2.5 flex-wrap">
              <Link to={`/admin/indicadores/${ind.indicador_id}`} data-testid={`ver-${ind.id_publico}`} className="px-3 py-2 rounded-lg bg-navy text-white text-[10px] font-extrabold active:scale-95 transition-transform">
                VER PERFIL
              </Link>
              {ind.status === "aguardando_aprovacao" && (
                <>
                  <button onClick={() => aprovar(ind)} data-testid={`aprovar-${ind.id_publico}`} className="px-3 py-2 rounded-lg bg-emerald-500 text-white text-[10px] font-extrabold flex items-center gap-1 active:scale-95 transition-transform">
                    <CheckCircle2 className="w-3 h-3" /> APROVAR
                  </button>
                  <button onClick={() => setRejeitarId(rejeitarId === ind.indicador_id ? null : ind.indicador_id)} data-testid={`rejeitar-${ind.id_publico}`} className="px-3 py-2 rounded-lg bg-red-100 text-red-600 text-[10px] font-extrabold flex items-center gap-1 active:scale-95 transition-transform">
                    <XCircle className="w-3 h-3" /> REJEITAR
                  </button>
                </>
              )}
              {waLink(ind.telefone) && (
                <a href={waLink(ind.telefone)} target="_blank" rel="noopener noreferrer" data-testid={`whatsapp-${ind.id_publico}`} className="px-3 py-2 rounded-lg bg-[#25D366] text-white text-[10px] font-extrabold flex items-center gap-1 active:scale-95 transition-transform">
                  <MessageCircle className="w-3 h-3" /> WHATSAPP
                </a>
              )}
            </div>
            {rejeitarId === ind.indicador_id && (
              <div className="mt-2 flex gap-2" data-testid={`rejeitar-form-${ind.id_publico}`}>
                <input className={`${inputCls} flex-1 !py-2`} placeholder="Motivo da rejeição (opcional)" value={motivo} onChange={(e) => setMotivo(e.target.value)} data-testid={`rejeitar-motivo-${ind.id_publico}`} />
                <button onClick={() => rejeitar(ind)} data-testid={`rejeitar-confirmar-${ind.id_publico}`} className="px-3 py-2 rounded-lg bg-red-600 text-white text-[10px] font-extrabold">CONFIRMAR</button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function FinanceiroTab() {
  const [cfg, setCfg] = useState(null);
  const [hist, setHist] = useState([]);
  const [form, setForm] = useState({ comissao_frete_pct: "", comissao_mudanca_pct: "", justificativa: "" });
  const [comissoes, setComissoes] = useState([]);
  const [pagId, setPagId] = useState(null);
  const [pagForm, setPagForm] = useState({ valor: "", metodo: "pix", transacao_id: "", data: "" });
  const [corId, setCorId] = useState(null);
  const [corForm, setCorForm] = useState({ novo_valor: "", justificativa: "" });

  const load = useCallback(() => {
    api.get("/admin/indicadores-config").then((r) => {
      setCfg(r.data.config);
      setHist(r.data.historico || []);
      setForm((f) => ({ ...f, comissao_frete_pct: r.data.config.comissao_frete_pct, comissao_mudanca_pct: r.data.config.comissao_mudanca_pct }));
    }).catch(() => {});
    api.get("/admin/comissoes").then((r) => setComissoes(r.data.comissoes || [])).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);

  const salvarConfig = async (e) => {
    e.preventDefault();
    try {
      await api.put("/admin/indicadores-config", {
        comissao_frete_pct: Number(form.comissao_frete_pct) || 0,
        comissao_mudanca_pct: Number(form.comissao_mudanca_pct) || 0,
        justificativa: form.justificativa,
      });
      toast.success("Configurações salvas com auditoria.");
      setForm((f) => ({ ...f, justificativa: "" }));
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Falha ao salvar");
    }
  };

  const registrarPagamento = async (c) => {
    try {
      await api.post(`/admin/comissoes/${c.comissao_id}/registrar-pagamento`, {
        valor: Number(pagForm.valor) || c.valor,
        metodo: pagForm.metodo,
        transacao_id: pagForm.transacao_id,
        data: pagForm.data,
      });
      toast.success("Pagamento registrado!");
      setPagId(null);
      setPagForm({ valor: "", metodo: "pix", transacao_id: "", data: "" });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Falha ao registrar pagamento");
    }
  };

  const corrigir = async (c) => {
    try {
      await api.post(`/admin/comissoes/${c.comissao_id}/corrigir`, { novo_valor: Number(corForm.novo_valor), justificativa: corForm.justificativa });
      toast.success("Comissão corrigida com justificativa registrada.");
      setCorId(null);
      setCorForm({ novo_valor: "", justificativa: "" });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Falha ao corrigir");
    }
  };

  const cancelar = async (c) => {
    const motivo = window.prompt("Motivo do cancelamento/estorno (obrigatório):");
    if (!motivo) return;
    try {
      await api.post(`/admin/comissoes/${c.comissao_id}/cancelar`, { motivo });
      toast.success("Comissão cancelada.");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Falha ao cancelar");
    }
  };

  return (
    <div className="px-4 space-y-4" data-testid="admin-financeiro-tab">
      <form onSubmit={salvarConfig} className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4 space-y-3" data-testid="ind-config-form">
        <h2 className="text-xs font-bold tracking-wider uppercase text-slate-400">Configurações financeiras dos indicadores</h2>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="text-[10px] font-semibold text-slate-500 uppercase">Comissão frete (%)</label>
            <input type="number" step="0.1" min="0" max="100" className={`${inputCls} mt-1`} data-testid="cfg-frete-pct" value={form.comissao_frete_pct} onChange={(e) => setForm((f) => ({ ...f, comissao_frete_pct: e.target.value }))} />
          </div>
          <div>
            <label className="text-[10px] font-semibold text-slate-500 uppercase">Comissão mudança (%)</label>
            <input type="number" step="0.1" min="0" max="100" className={`${inputCls} mt-1`} data-testid="cfg-mudanca-pct" value={form.comissao_mudanca_pct} onChange={(e) => setForm((f) => ({ ...f, comissao_mudanca_pct: e.target.value }))} />
          </div>
        </div>
        <p className="text-[10px] text-slate-400">Base de cálculo: valor total pago pelo cliente pelo serviço elegível. Percentuais valem para novas contratações (sem efeito retroativo).</p>
        <input className={inputCls} placeholder="Justificativa da alteração (obrigatória ao mudar)" data-testid="cfg-justificativa" value={form.justificativa} onChange={(e) => setForm((f) => ({ ...f, justificativa: e.target.value }))} />
        <button type="submit" data-testid="cfg-salvar-btn" className="w-full py-3 rounded-xl bg-navy text-white text-xs font-extrabold active:scale-95 transition-transform">SALVAR CONFIGURAÇÕES</button>
        {hist.length > 0 && (
          <div className="pt-2 border-t border-slate-100" data-testid="cfg-historico">
            <p className="text-[10px] font-bold text-slate-400 uppercase mb-1.5">Histórico de alterações</p>
            {hist.slice(0, 8).map((h, i) => (
              <p key={i} className="text-[10px] text-slate-500 py-1 border-b border-slate-50 last:border-0">
                {fmtDt(h.em)} · {h.admin}: {(h.mudancas || []).map((m) => `${m.campo === "comissao_frete_pct" ? "Frete" : "Mudança"} ${m.anterior}%→${m.novo}%`).join(" · ")} — {h.justificativa}
              </p>
            ))}
          </div>
        )}
      </form>

      <div className="space-y-2" data-testid="comissoes-lista">
        <h2 className="text-xs font-bold tracking-wider uppercase text-slate-400 px-1">Comissões</h2>
        {comissoes.length === 0 && <p className="text-xs text-slate-400 text-center py-4">Nenhuma comissão até o momento.</p>}
        {comissoes.map((c) => (
          <div key={c.comissao_id} className="bg-white rounded-2xl border border-slate-100 shadow-sm p-3" data-testid={`comissao-${c.comissao_id}`}>
            <div className="flex items-center justify-between gap-2">
              <p className="text-xs font-bold text-navy truncate">{c.indicador_nome} <span className="text-slate-400 font-semibold">{c.id_publico}</span></p>
              <span className={`text-[9px] font-extrabold px-2 py-0.5 rounded-full whitespace-nowrap ${COM_STYLE[c.status]}`} data-testid={`comissao-status-${c.comissao_id}`}>{c.status_label}</span>
            </div>
            <p className="text-[10px] text-slate-500 mt-1">
              {c.tipo === "frete" ? "FRETE" : "MUDANÇA"} · base {brl(c.base_calculo)} · {c.percentual}% → <strong className="text-navy">{brl(c.valor)}</strong>
            </p>
            {c.prazo_pagamento && c.status === "liberada" && (
              <p className="text-[10px] text-blue-600 mt-0.5">Pagar até {String(c.prazo_pagamento).split("-").reverse().join("/")} (1 dia útil da liberação)</p>
            )}
            {c.status === "paga" && c.pagamento && (
              <p className="text-[10px] text-emerald-600 mt-0.5">Pago em {fmtDt(c.paga_em)} · {c.pagamento.metodo}{c.pagamento.transacao_id ? ` · ${c.pagamento.transacao_id}` : ""} · por {c.pagamento.registrado_por}</p>
            )}
            {c.status === "liberada" && (
              <div className="flex gap-1.5 mt-2 flex-wrap">
                <button onClick={() => { setPagId(pagId === c.comissao_id ? null : c.comissao_id); setPagForm({ valor: String(c.valor), metodo: "pix", transacao_id: "", data: "" }); }} data-testid={`pagar-${c.comissao_id}`} className="px-3 py-2 rounded-lg bg-emerald-500 text-white text-[10px] font-extrabold flex items-center gap-1 active:scale-95 transition-transform">
                  <Wallet className="w-3 h-3" /> REGISTRAR PAGAMENTO
                </button>
                <button onClick={() => setCorId(corId === c.comissao_id ? null : c.comissao_id)} data-testid={`corrigir-${c.comissao_id}`} className="px-3 py-2 rounded-lg bg-amber-100 text-amber-700 text-[10px] font-extrabold active:scale-95 transition-transform">CORRIGIR</button>
                <button onClick={() => cancelar(c)} data-testid={`cancelar-${c.comissao_id}`} className="px-3 py-2 rounded-lg bg-red-100 text-red-600 text-[10px] font-extrabold flex items-center gap-1 active:scale-95 transition-transform">
                  <Ban className="w-3 h-3" /> CANCELAR
                </button>
              </div>
            )}
            {c.status === "aguardando_conclusao" && (
              <div className="flex gap-1.5 mt-2">
                <button onClick={() => setCorId(corId === c.comissao_id ? null : c.comissao_id)} data-testid={`corrigir-${c.comissao_id}`} className="px-3 py-2 rounded-lg bg-amber-100 text-amber-700 text-[10px] font-extrabold active:scale-95 transition-transform">CORRIGIR</button>
                <button onClick={() => cancelar(c)} data-testid={`cancelar-${c.comissao_id}`} className="px-3 py-2 rounded-lg bg-red-100 text-red-600 text-[10px] font-extrabold flex items-center gap-1 active:scale-95 transition-transform">
                  <Ban className="w-3 h-3" /> CANCELAR
                </button>
              </div>
            )}
            {pagId === c.comissao_id && (
              <div className="mt-2 rounded-xl bg-slate-50 border border-slate-100 p-3 space-y-2" data-testid={`pagar-form-${c.comissao_id}`}>
                <p className="text-[10px] text-slate-500 font-semibold">
                  Conferir antes de pagar: {c.indicador_nome} ({c.id_publico}) · Pix {c.pix?.tipo || "-"}: {c.pix?.chave || "não cadastrada"} · titular {c.pix?.titular_nome || "-"}
                </p>
                <div className="grid grid-cols-2 gap-2">
                  <input type="number" step="0.01" className={inputCls} data-testid={`pag-valor-${c.comissao_id}`} value={pagForm.valor} onChange={(e) => setPagForm((f) => ({ ...f, valor: e.target.value }))} placeholder="Valor pago" />
                  <input type="date" className={inputCls} data-testid={`pag-data-${c.comissao_id}`} value={pagForm.data} onChange={(e) => setPagForm((f) => ({ ...f, data: e.target.value }))} />
                </div>
                <input className={inputCls} placeholder="ID da transação / comprovante (opcional)" data-testid={`pag-transacao-${c.comissao_id}`} value={pagForm.transacao_id} onChange={(e) => setPagForm((f) => ({ ...f, transacao_id: e.target.value }))} />
                <button onClick={() => registrarPagamento(c)} data-testid={`pag-confirmar-${c.comissao_id}`} className="w-full py-2.5 rounded-lg bg-emerald-600 text-white text-[10px] font-extrabold">CONFIRMAR PAGAMENTO EFETUADO</button>
              </div>
            )}
            {corId === c.comissao_id && (
              <div className="mt-2 rounded-xl bg-slate-50 border border-slate-100 p-3 space-y-2" data-testid={`corrigir-form-${c.comissao_id}`}>
                <input type="number" step="0.01" className={inputCls} placeholder={`Novo valor (atual ${brl(c.valor)})`} data-testid={`cor-valor-${c.comissao_id}`} value={corForm.novo_valor} onChange={(e) => setCorForm((f) => ({ ...f, novo_valor: e.target.value }))} />
                <input className={inputCls} placeholder="Justificativa (obrigatória)" data-testid={`cor-just-${c.comissao_id}`} value={corForm.justificativa} onChange={(e) => setCorForm((f) => ({ ...f, justificativa: e.target.value }))} />
                <button onClick={() => corrigir(c)} data-testid={`cor-confirmar-${c.comissao_id}`} className="w-full py-2.5 rounded-lg bg-amber-500 text-white text-[10px] font-extrabold">SALVAR CORREÇÃO</button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function AtendimentoTab() {
  const [lista, setLista] = useState([]);
  const [thread, setThread] = useState(null);
  const [texto, setTexto] = useState("");

  const load = useCallback(() => {
    api.get("/admin/atendimentos").then((r) => setLista(r.data.atendimentos || [])).catch(() => {});
  }, []);

  useEffect(() => {
    load();
    const i = setInterval(load, 20000);
    return () => clearInterval(i);
  }, [load]);

  const abrir = (indicador_id) => {
    api.get(`/admin/atendimentos/${indicador_id}`).then((r) => {
      setThread({ indicador_id, ...r.data });
      load();
    }).catch(() => {});
  };

  const responder = async (e) => {
    e.preventDefault();
    if (!texto.trim() || !thread) return;
    try {
      await api.post(`/admin/atendimentos/${thread.indicador_id}/responder`, { texto: texto.trim() });
      setTexto("");
      abrir(thread.indicador_id);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Falha ao responder");
    }
  };

  const mudarStatus = async (status) => {
    try {
      await api.put(`/admin/atendimentos/${thread.indicador_id}/status`, { status });
      toast.success(`Atendimento: ${status.replace("_", " ")}`);
      abrir(thread.indicador_id);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Falha ao atualizar");
    }
  };

  if (thread) {
    return (
      <div className="px-4" data-testid="atendimento-thread">
        <button onClick={() => setThread(null)} className="text-xs text-navy font-bold underline mb-2" data-testid="thread-voltar">← Voltar aos atendimentos</button>
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4">
          <div className="flex items-center gap-2 mb-3">
            {thread.indicador?.foto && <img src={fotoUrl(thread.indicador.id_publico)} alt="" className="w-8 h-8 rounded-full object-cover" onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = "/logo.jpg"; e.currentTarget.classList.remove("object-cover"); e.currentTarget.classList.add("object-contain", "bg-white", "p-1"); }} />}
            <p className="text-sm font-bold text-navy">{thread.indicador?.nome} <span className="text-slate-400 text-xs">{thread.indicador?.id_publico}</span></p>
          </div>
          <div className="max-h-72 overflow-y-auto space-y-2 mb-3" data-testid="thread-msgs">
            {(thread.mensagens || []).map((m) => (
              <div key={m.msg_id} className={`max-w-[85%] rounded-2xl px-3 py-2 text-xs ${m.remetente === "admin" ? "ml-auto bg-navy text-white" : "bg-gold-light/60 text-navy"}`} data-testid={`adm-msg-${m.msg_id}`}>
                <p className="whitespace-pre-wrap">{m.texto}</p>
                <p className={`text-[9px] mt-1 ${m.remetente === "admin" ? "text-slate-300" : "text-slate-500"}`}>{m.remetente === "admin" ? "ADM" : thread.indicador?.nome} · {fmtDt(m.em)}</p>
              </div>
            ))}
          </div>
          <form onSubmit={responder} className="flex gap-2">
            <input className={`${inputCls} flex-1`} placeholder="Responder ao indicador..." data-testid="thread-input" value={texto} onChange={(e) => setTexto(e.target.value)} />
            <button type="submit" data-testid="thread-enviar-btn" className="px-4 rounded-xl bg-[#F5B301] text-navy active:scale-95 transition-transform" aria-label="Responder"><Send className="w-4 h-4" /></button>
          </form>
          <div className="flex gap-2 mt-3">
            <button onClick={() => mudarStatus("em_andamento")} data-testid="thread-em-andamento" className="flex-1 py-2 rounded-lg bg-blue-100 text-blue-700 text-[10px] font-extrabold">EM ANDAMENTO</button>
            <button onClick={() => mudarStatus("concluido")} data-testid="thread-concluir" className="flex-1 py-2 rounded-lg bg-emerald-100 text-emerald-700 text-[10px] font-extrabold">MARCAR CONCLUÍDO</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="px-4 space-y-2" data-testid="atendimentos-lista">
      <div className="flex justify-end">
        <button onClick={load} className="text-[10px] font-bold text-navy flex items-center gap-1" data-testid="atendimentos-refresh"><RefreshCw className="w-3 h-3" /> Atualizar</button>
      </div>
      {lista.length === 0 && <p className="text-xs text-slate-400 text-center py-6">Nenhum atendimento até o momento.</p>}
      {lista.map((a) => (
        <button key={a.indicador_id} onClick={() => abrir(a.indicador_id)} data-testid={`atendimento-${a.indicador_id}`}
          className="w-full text-left bg-white rounded-2xl border border-slate-100 shadow-sm p-3 flex items-center gap-3 active:scale-[0.99] transition-transform">
          {a.foto ? <img src={fotoUrl(a.id_publico)} alt="" className="w-10 h-10 rounded-full object-cover" onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = "/logo.jpg"; e.currentTarget.classList.remove("object-cover"); e.currentTarget.classList.add("object-contain", "bg-white", "p-1"); }} /> : <div className="w-10 h-10 rounded-full bg-navy text-[#F5B301] flex items-center justify-center font-extrabold">{a.nome?.[0]}</div>}
          <div className="flex-1 min-w-0">
            <p className="text-xs font-bold text-navy truncate">{a.nome} <span className="text-slate-400 font-semibold">{a.id_publico}</span></p>
            <p className="text-[10px] text-slate-500 truncate">{a.ultima_msg || "—"}</p>
            <p className="text-[9px] text-slate-400">{fmtDt(a.ultima_msg_em)} · {a.status === "concluido" ? "CONCLUÍDO" : a.status === "em_andamento" ? "EM ANDAMENTO" : "NOVO"}</p>
          </div>
          {a.nao_lidas > 0 && <span className="min-w-[20px] h-5 px-1.5 rounded-full bg-red-500 text-white text-[10px] font-extrabold flex items-center justify-center" data-testid={`nao-lidas-${a.indicador_id}`}>{a.nao_lidas}</span>}
        </button>
      ))}
    </div>
  );
}

function RankingTab() {
  const [dados, setDados] = useState(null);
  const [mes, setMes] = useState("");
  const [premiacoes, setPremiacoes] = useState([]);
  const [premioValor, setPremioValor] = useState("");
  const [premioJust, setPremioJust] = useState("");
  const [salvando, setSalvando] = useState(false);
  const [apurando, setApurando] = useState(false);
  const [pagandoId, setPagandoId] = useState(null);
  const [pagValor, setPagValor] = useState("");
  const [pagMetodo, setPagMetodo] = useState("pix");
  const [pagTx, setPagTx] = useState("");

  const load = useCallback(async (m = "") => {
    try {
      const r = await api.get("/admin/indicadores-ranking", { params: m ? { mes: m } : {} });
      setDados(r.data);
      setPremioValor(String(r.data.premio_valor ?? 0));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Falha ao carregar o ranking");
    }
    api.get("/admin/indicadores-premiacoes").then((r) => setPremiacoes(r.data.premiacoes || [])).catch(() => {});
  }, []);

  useEffect(() => { load(mes); }, [mes, load]);

  const salvarPremio = async () => {
    if (salvando) return;
    setSalvando(true);
    try {
      const r = await api.put("/admin/indicadores-premio", { valor: parseFloat(String(premioValor).replace(",", ".")) || 0, justificativa: premioJust });
      toast.success(r.data.alterado ? "Prêmio do 1º lugar atualizado!" : "Valor do prêmio mantido (sem alteração).");
      setPremioJust("");
      load(mes);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Falha ao salvar o prêmio");
    } finally { setSalvando(false); }
  };

  const apurar = async () => {
    if (!dados?.mes || apurando) return;
    if (!window.confirm(`Apurar o vencedor do ranking de ${dados.mes} agora?\n\nO prêmio será registrado na carteira do 1º lugar como "a pagar".`)) return;
    setApurando(true);
    try {
      const r = await api.post("/admin/indicadores-premio/apurar", { mes: dados.mes });
      if (r.data.duplicado) toast.info("Este mês já foi apurado — prêmio existente preservado (sem duplicidade).");
      else if (r.data.sem_vencedor) toast.info(`Sem apuração: ${r.data.motivo}`);
      else toast.success(`🏆 Prêmio apurado: ${r.data.premio.nome} — ${brl(r.data.premio.valor)}`);
      load(mes);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Falha ao apurar");
    } finally { setApurando(false); }
  };

  const registrarPagamento = async (premioId) => {
    try {
      await api.post(`/admin/premiacoes/${premioId}/registrar-pagamento`, {
        valor: parseFloat(String(pagValor).replace(",", ".")) || 0, metodo: pagMetodo, transacao_id: pagTx,
      });
      toast.success("Pagamento do prêmio registrado!");
      setPagandoId(null); setPagValor(""); setPagTx("");
      load(mes);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Falha ao registrar pagamento");
    }
  };

  const Lista = ({ lista, tid }) => (
    <div className="space-y-2" data-testid={tid}>
      {lista.length === 0 && <p className="text-xs text-slate-400 text-center py-3">Nenhum indicador no ranking.</p>}
      {lista.map((r) => (
        <div key={r.indicador_id} className="flex items-center gap-2.5 rounded-xl border border-slate-100 bg-slate-50 p-2.5" data-testid={`${tid}-row-${r.id_publico}`}>
          <span className={`w-7 h-7 rounded-full flex items-center justify-center text-[11px] font-extrabold shrink-0 ${r.posicao === 1 ? "bg-[#F5B301] text-navy" : "bg-slate-200 text-slate-600"}`}>{r.posicao}º</span>
          {r.foto ? (
            <img src={fotoUrl(r.id_publico)} alt="" className="w-9 h-9 rounded-full object-cover border border-slate-200"
              onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = "/logo.jpg"; e.currentTarget.classList.remove("object-cover"); e.currentTarget.classList.add("object-contain", "bg-white", "p-1"); }} />
          ) : (
            <div className="w-9 h-9 rounded-full bg-navy text-[#F5B301] flex items-center justify-center text-xs font-extrabold">{r.nome?.[0]}</div>
          )}
          <div className="flex-1 min-w-0">
            <p className="text-xs font-bold text-navy truncate">{r.nome}</p>
            <p className="text-[9px] text-slate-400">{r.id_publico} · {r.total_indicacoes} indicação(ões)</p>
          </div>
          <div className="text-right shrink-0">
            <p className="text-sm font-extrabold text-navy">{r.contratos_fechados}</p>
            <p className="text-[8px] text-slate-400 uppercase">fechados</p>
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <div className="px-4 space-y-4" data-testid="ranking-tab">
      <section className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4">
        <h2 className="text-xs font-bold tracking-wider uppercase text-slate-400 mb-2 flex items-center gap-1.5"><Trophy className="w-3.5 h-3.5" /> Prêmio do 1º lugar (mensal)</h2>
        <input data-testid="premio-valor-input" inputMode="decimal" className={inputCls} placeholder="Valor do prêmio (R$)" value={premioValor} onChange={(e) => setPremioValor(e.target.value)} />
        <input data-testid="premio-just-input" className={`${inputCls} mt-2`} placeholder="Justificativa (obrigatória ao alterar)" value={premioJust} onChange={(e) => setPremioJust(e.target.value)} />
        <button onClick={salvarPremio} disabled={salvando} data-testid="premio-salvar-btn" className="mt-2 w-full py-3 rounded-xl bg-navy text-white text-xs font-extrabold active:scale-95 transition-transform disabled:opacity-50">
          {salvando ? "SALVANDO..." : "SALVAR VALOR DO PRÊMIO"}
        </button>
        <p className="text-[10px] text-slate-400 mt-1.5">O valor vigente é exibido no ranking dos indicadores e usado na apuração automática do dia 1º.</p>
      </section>

      <section className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4">
        <div className="flex items-center justify-between mb-2 gap-2">
          <h2 className="text-xs font-bold tracking-wider uppercase text-slate-400">Ranking mensal</h2>
          <input type="month" data-testid="ranking-mes-input" className="px-2 py-1.5 rounded-lg border border-slate-200 text-xs" value={mes || dados?.mes || ""} onChange={(e) => setMes(e.target.value)} />
        </div>
        {dados ? <Lista lista={dados.mensal || []} tid="ranking-mensal-list" /> : <p className="text-xs text-slate-400">Carregando...</p>}
        <button onClick={apurar} disabled={apurando} data-testid="apurar-btn" className="mt-3 w-full py-3 rounded-xl bg-[#F5B301] text-navy text-xs font-extrabold active:scale-95 transition-transform disabled:opacity-50">
          {apurando ? "APURANDO..." : `APURAR VENCEDOR DE ${dados?.mes || ""} AGORA`}
        </button>
        <p className="text-[10px] text-slate-400 mt-1.5">A apuração automática roda todo dia 1º. Este botão antecipa/repete sem duplicar (um prêmio por mês).</p>
      </section>

      <section className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4">
        <h2 className="text-xs font-bold tracking-wider uppercase text-slate-400 mb-2">Ranking geral (desde o início)</h2>
        {dados ? <Lista lista={dados.geral || []} tid="ranking-geral-list" /> : <p className="text-xs text-slate-400">Carregando...</p>}
      </section>

      <section className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4">
        <h2 className="text-xs font-bold tracking-wider uppercase text-slate-400 mb-2">Histórico de vencedores e prêmios</h2>
        {premiacoes.length === 0 && <p className="text-xs text-slate-400" data-testid="premiacoes-vazio">Nenhuma premiação apurada ainda.</p>}
        <div className="space-y-2" data-testid="premiacoes-list">
          {premiacoes.map((p) => (
            <div key={p.premio_id} className="rounded-xl border border-slate-100 bg-slate-50 p-3" data-testid={`premiacao-${p.premio_id}`}>
              <div className="flex items-center justify-between gap-2">
                <p className="text-[11px] font-bold text-navy">🏆 {p.mes_referencia} · {p.nome} ({p.id_publico})</p>
                <span className={`text-[9px] font-extrabold px-2 py-0.5 rounded-full whitespace-nowrap ${COM_STYLE[p.status] || "bg-slate-100 text-slate-500"}`} data-testid={`premiacao-status-${p.premio_id}`}>{p.status_label}</span>
              </div>
              <p className="text-[10px] text-slate-500 mt-0.5">{brl(p.valor)} · {p.contratos_fechados} contrato(s) fechado(s) · apurado em {fmtDt(p.created_at)} ({p.origem})</p>
              {p.pagamento && <p className="text-[10px] text-emerald-600 mt-0.5">Pago em {p.pagamento.data} · {p.pagamento.metodo}{p.pagamento.transacao_id ? ` · ${p.pagamento.transacao_id}` : ""}</p>}
              {p.status === "liberada" && (
                pagandoId === p.premio_id ? (
                  <div className="mt-2 space-y-1.5" data-testid={`premiacao-pag-form-${p.premio_id}`}>
                    <div className="flex gap-1.5">
                      <input className="flex-1 px-2.5 py-2 rounded-lg border border-slate-200 text-xs" inputMode="decimal" placeholder={`Valor pago (R$) — prêmio: ${brl(p.valor)}`} value={pagValor} onChange={(e) => setPagValor(e.target.value)} data-testid={`premiacao-pag-valor-${p.premio_id}`} />
                      <select className="px-2 py-2 rounded-lg border border-slate-200 text-xs" value={pagMetodo} onChange={(e) => setPagMetodo(e.target.value)} data-testid={`premiacao-pag-metodo-${p.premio_id}`}>
                        <option value="pix">Pix</option>
                        <option value="ted">TED</option>
                        <option value="dinheiro">Dinheiro</option>
                      </select>
                    </div>
                    <input className="w-full px-2.5 py-2 rounded-lg border border-slate-200 text-xs" placeholder="ID da transação (opcional)" value={pagTx} onChange={(e) => setPagTx(e.target.value)} data-testid={`premiacao-pag-tx-${p.premio_id}`} />
                    <div className="flex gap-1.5">
                      <button onClick={() => registrarPagamento(p.premio_id)} data-testid={`premiacao-pag-confirmar-${p.premio_id}`} className="flex-1 py-2 rounded-lg bg-emerald-500 text-white text-[10px] font-extrabold active:scale-95 transition-transform">CONFIRMAR PAGAMENTO</button>
                      <button onClick={() => setPagandoId(null)} className="px-3 py-2 rounded-lg bg-slate-200 text-slate-600 text-[10px] font-extrabold">CANCELAR</button>
                    </div>
                  </div>
                ) : (
                  <button onClick={() => { setPagandoId(p.premio_id); setPagValor(String(p.valor)); }} data-testid={`premiacao-pagar-${p.premio_id}`} className="mt-2 w-full py-2 rounded-lg bg-navy text-[#F5B301] text-[10px] font-extrabold active:scale-95 transition-transform">
                    REGISTRAR PAGAMENTO
                  </button>
                )
              )}
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

function ClientesTab() {
  const [clientes, setClientes] = useState(null);
  const [busca, setBusca] = useState("");
  useEffect(() => {
    api.get("/admin/indicadores-clientes").then((r) => setClientes(r.data.clientes || [])).catch(() => toast.error("Falha ao carregar os clientes indicados"));
  }, []);
  const SIT_STYLE = { pendente: "bg-amber-100 text-amber-700", em_andamento: "bg-blue-100 text-blue-700", fechado: "bg-emerald-100 text-emerald-700", cancelado: "bg-red-100 text-red-600" };
  const SIT_LABEL = { pendente: "PENDENTE", em_andamento: "EM ANDAMENTO", fechado: "FECHADO", cancelado: "CANCELADO" };
  const q = busca.trim().toLowerCase();
  const lista = (clientes || []).filter((c) => !q || c.cliente_nome.toLowerCase().includes(q) || c.codigo.toLowerCase().includes(q) || c.indicador_nome.toLowerCase().includes(q));
  return (
    <div className="px-4 space-y-3" data-testid="clientes-tab">
      <input className={inputCls} placeholder="Buscar por cliente, indicador ou código..." value={busca} onChange={(e) => setBusca(e.target.value)} data-testid="clientes-busca" />
      <div className="space-y-2" data-testid="clientes-indicados-list">
        {clientes === null && <p className="text-xs text-slate-400 text-center py-4">Carregando...</p>}
        {clientes !== null && lista.length === 0 && <p className="text-xs text-slate-400 text-center py-4" data-testid="clientes-vazio">Nenhum cliente indicado encontrado.</p>}
        {lista.map((c) => (
          <div key={c.indicacao_id} className="bg-white rounded-2xl border border-slate-100 shadow-sm p-3" data-testid={`cliente-indicado-${c.indicacao_id}`}>
            <div className="flex items-center justify-between gap-2">
              <p className="text-xs font-bold text-navy truncate">{c.cliente_nome}</p>
              <span className={`text-[9px] font-extrabold px-2 py-0.5 rounded-full whitespace-nowrap ${SIT_STYLE[c.situacao] || "bg-slate-100 text-slate-500"}`} data-testid={`cliente-situacao-${c.indicacao_id}`}>{SIT_LABEL[c.situacao] || c.situacao}</span>
            </div>
            <p className="text-[10px] text-slate-500 mt-0.5">Indicado por <strong>{c.indicador_nome}</strong> ({c.codigo}) · {(c.tipo || "").toUpperCase() || "—"} · {fmtDt(c.data)}</p>
            {c.quote_id && <Link to={`/admin/orcamento/${c.quote_id}`} className="text-[10px] font-bold text-navy underline" data-testid={`cliente-quote-${c.indicacao_id}`}>Ver orçamento</Link>}
          </div>
        ))}
      </div>
    </div>
  );
}

function IndicadoresInner({ user }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get("f") || "indicadores";
  const setTab = (t) => setSearchParams(t === "indicadores" ? {} : { f: t });

  return (
    <AdminShell user={user} title="Gestão de Indicadores">
      <div className="px-4 pb-3 flex gap-2" data-testid="indicadores-tabs">
        {[
          { v: "indicadores", l: "Indicadores", icon: Users },
          { v: "financeiro", l: "Financeiro", icon: Wallet },
          { v: "ranking", l: "Ranking", icon: Trophy },
          { v: "clientes", l: "Clientes", icon: UserCheck },
          { v: "atendimento", l: "Atendimento", icon: MessageCircle },
        ].map((t) => (
          <button key={t.v} onClick={() => setTab(t.v)} data-testid={`tab-${t.v}`}
            className={`flex-1 py-2.5 rounded-xl text-[10px] font-extrabold flex items-center justify-center gap-1.5 active:scale-95 transition-transform ${tab === t.v ? "bg-navy text-[#F5B301]" : "bg-white text-slate-500 border border-slate-200"}`}>
            <t.icon className="w-3.5 h-3.5" /> {t.l}
          </button>
        ))}
      </div>
      <div className="px-4 pb-3 -mt-1 flex justify-end">
        <Link to="/admin/divulgacao" data-testid="link-divulgacao" className="inline-flex items-center gap-1 text-[11px] font-extrabold text-navy underline underline-offset-2">
          <Images className="w-3.5 h-3.5" /> Gerenciar Imagens de Divulgação
        </Link>
      </div>
      {tab === "indicadores" && <IndicadoresTab />}
      {tab === "financeiro" && <FinanceiroTab />}
      {tab === "ranking" && <RankingTab />}
      {tab === "clientes" && <ClientesTab />}
      {tab === "atendimento" && <AtendimentoTab />}
    </AdminShell>
  );
}

export default function AdminIndicadores() {
  return <AdminGate>{(user) => <IndicadoresInner user={user} />}</AdminGate>;
}
